/**
 * F-027 — Admin shell state-preservation policy (load-bearing).
 *
 * This comment block documents how the admin shell interacts with three
 * categories of client state during navigation. Future contributors — do
 * not delete this without reading
 * .dukanti/research/feature-f-027-sidebar-admin-shell/02-design.md first.
 *
 * 1. URL-backed state (search params, path params, location.state)
 *    Survives all shell navigation — React Router owns the URL, the
 *    shell does not rewrite it. Example: /dashboard/settings?fix=contact
 *    remains intact when the merchant navigates away and returns via
 *    browser back. The blocker predicate (§4.1) does NOT short-circuit
 *    on equal pathname, so same-route sidebar clicks on a dirty form
 *    still fire the confirmation dialog — `?fix=contact` is never
 *    silently wiped.
 *
 * 2. Unsaved form drafts
 *    Forms inside the shell opt into the dirty-form guard by calling
 *    useNavBlocker(isDirty) from apps/admin/src/hooks/use-nav-blocker.tsx.
 *    For RHF-only forms, isDirty = formState.isDirty. For forms whose
 *    state extends beyond RHF (e.g. product-form's image slot state),
 *    pass an aggregate: formState.isDirty || slotsDirty. The blocker
 *    fires on sidebar clicks, browser back/forward, and programmatic
 *    navigate() — intercepting React Router navigation only. Plain
 *    <a href> outside the router, window.location assignments, and
 *    tab-close bypass the guard; add a beforeunload handler in a
 *    follow-up if that coverage is needed (F-027 out of scope).
 *
 *    Single-form assumption: the context stores ONE isDirty flag. No
 *    admin route mounts two dirty forms simultaneously today. A
 *    dev-mode console.warn fires if that changes. When/if a future
 *    feature needs concurrent dirty forms, widen useNavBlocker to a
 *    Map<callerId, boolean> registry without breaking its public
 *    signature.
 *
 *    Signed-out precedence: if the Supabase session transitions to
 *    null (token expiry, admin revocation), the blocker proactively
 *    proceeds so AuthGuard's <Navigate to="/auth/signin"/> is NOT
 *    intercepted. Unsaved in-memory form state IS lost in this case —
 *    this is a deliberate trade-off favoring security precedence over
 *    dirty-state retention. Do NOT re-arm the blocker against auth
 *    transitions. SIGNED_OUT always wins over the dirty-form guard.
 *
 * 3. React Query cache behavior
 *    Per-tab only. The shell does not promise cross-tab consistency.
 *    React Query's refetchOnWindowFocus: true catches divergence on
 *    tab refocus (documented in use-store.ts §Cross-tab cache isolation).
 *
 *    The cache-invalidation audit (F-027 §2) pins every current
 *    source-mutation → consumer-screen pair. If a new mutation lands,
 *    update the audit matrix in admin-shell-cache-audit.test.tsx.
 *
 * Links:
 *   - Design doc: .dukanti/research/feature-f-027-sidebar-admin-shell/02-design.md
 *   - PRD entry:  .dukanti/prds/admin-shell.md § F-027
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { signOut, useTranslation, useAuthSession } from '@dukanti/shared';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { NavBlockerProvider } from '@/hooks/use-nav-blocker';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';
import { AdminShellSkipLink } from './admin-shell-skip-link';
import { AdminShellTopbar } from './admin-shell-topbar';
import { AdminShellSidebar } from './admin-shell-sidebar';
import { AdminShellUnsavedDialog } from './admin-shell-unsaved-dialog';

/**
 * Persistent admin shell chrome — sidebar + top bar + skip-link + single
 * unsaved-changes confirmation dialog. Wraps the authenticated routes via
 * a parent `<Route element={<AdminShellLayout/>}>` in routes/index.tsx.
 *
 * /create-store is deliberately EXCLUDED from the shell: a merchant on
 * that route has no store yet, so the Dashboard nav affordance would
 * bounce them back via AuthGuard mode="require-store".
 *
 * Auth gate: the layout itself gates on session + store membership
 * before rendering any shell chrome. Unauthenticated or storeless
 * visitors see the loading spinner or a redirect — never the shell.
 * Closes ADMIN-TBD-shell-unauth-flash.
 */
export function AdminShellLayout() {
  const { t, dir } = useTranslation();
  const location = useLocation();
  const authSession = useAuthSession(supabase);
  const {
    storeId,
    accessState,
    merchantStatus,
    isLoading: isStoreLoading,
    isError,
    refetch,
  } = useCurrentStore(authSession);

  // --- Auth gate: prevent shell chrome from rendering until session + store
  // membership are confirmed. Closes ADMIN-TBD-shell-unauth-flash. ---

  // Still loading session or store membership
  if (authSession.isLoading || (authSession.session && isStoreLoading)) {
    return (
      <div
        data-testid="auth-guard-loading"
        className="bg-background flex min-h-screen items-center justify-center"
      >
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  // Not authenticated
  if (!authSession.session) {
    return <Navigate to="/auth/signin" state={{ from: location.pathname }} replace />;
  }

  // Store membership query failed
  if (isError) {
    return (
      <div
        data-testid="auth-guard-error"
        className="bg-background flex min-h-screen items-center justify-center"
      >
        <div className="text-center">
          <p className="text-foreground">{t('errors.networkFailed')}</p>
          <button type="button" className="mt-4 underline" onClick={() => refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!storeId) {
    if (merchantStatus === 'approved') {
      return <Navigate to="/create-store" replace />;
    }
    return <AdminShellAccessState state={accessState ?? 'pending'} />;
  }

  // Happy path: authenticated with a store — render shell chrome
  return (
    <div dir={dir} data-testid="admin-shell" className="bg-background text-foreground min-h-screen">
      <NavBlockerProvider>
        <SidebarProvider>
          <AdminShellSkipLink />
          <AdminShellSidebar
            storeId={storeId}
            userId={authSession.user?.id ?? null}
            userEmail={authSession.user?.email ?? null}
          />
          <SidebarInset>
            <AdminShellTopbar userId={authSession.user?.id ?? null} />
            <main id="main" tabIndex={-1}>
              <Outlet />
            </main>
          </SidebarInset>
          <AdminShellUnsavedDialog />
        </SidebarProvider>
      </NavBlockerProvider>
    </div>
  );
}

function AdminShellAccessState({ state }: { state: 'member' | 'pending' | 'unauthorized' }) {
  const { t } = useTranslation();
  const isPending = state === 'pending';
  const handleSignOut = (): void => {
    void signOut(supabase).finally(() => {
      window.location.assign('/auth/signin');
    });
  };

  return (
    <main
      data-testid={isPending ? 'admin-shell-access-pending' : 'admin-shell-access-unauthorized'}
      className="bg-background flex min-h-screen items-center justify-center px-4"
    >
      <section className="bg-card border-border max-w-md rounded-xl border p-6 text-center shadow-sm">
        <h1 className="font-display text-foreground text-2xl">
          {isPending
            ? t('admin.authBoundary.pendingTitle')
            : t('admin.authBoundary.unauthorizedTitle')}
        </h1>
        <p className="text-muted-foreground mt-3 text-sm leading-6">
          {isPending
            ? t('admin.authBoundary.pendingBody')
            : t('admin.authBoundary.unauthorizedBody')}
        </p>
        <button type="button" className="mt-5 underline" onClick={handleSignOut}>
          {t('admin.authBoundary.signOut')}
        </button>
      </section>
    </main>
  );
}
